import { useState, useCallback, useRef } from "react";
import { GripVertical } from "lucide-react";
import { useInView } from "@/hooks/useInView";

interface ImageCardProps {
  beforeGradient: string;
  afterGradient: string;
  beforeImage?: string;
  afterImage?: string;
}

const GESTURE_THRESHOLD = 8;

const ImageCard = ({ beforeGradient, afterGradient, beforeImage, afterImage }: ImageCardProps) => {
  const [inViewRef, isInView] = useInView({ rootMargin: "200px" });
  const [split, setSplit] = useState(90);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const gestureResolvedRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    (inViewRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [inViewRef]);

  const updateSplit = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSplit(percent);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    gestureResolvedRef.current = false;
    pointerIdRef.current = e.pointerId;
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (isDragging) {
      updateSplit(e.clientX);
      return;
    }
    if (gestureResolvedRef.current || !startPosRef.current) return;
    const deltaX = Math.abs(e.clientX - startPosRef.current.x);
    const deltaY = Math.abs(e.clientY - startPosRef.current.y);
    if (deltaX > deltaY && deltaX > GESTURE_THRESHOLD) {
      gestureResolvedRef.current = true;
      setIsDragging(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      if (containerRef.current) containerRef.current.style.touchAction = "none";
      updateSplit(e.clientX);
    } else if (deltaY > deltaX && deltaY > GESTURE_THRESHOLD) {
      gestureResolvedRef.current = true;
      startPosRef.current = null;
    }
  }, [isDragging, updateSplit]);

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
    startPosRef.current = null;
    gestureResolvedRef.current = false;
    pointerIdRef.current = null;
    if (containerRef.current) containerRef.current.style.touchAction = "pan-y";
  }, []);

  // Skeleton placeholder when not in view
  if (!isInView) {
    return (
      <div
        ref={setRefs}
        className="relative aspect-[9/16] rounded-xl overflow-hidden bg-muted animate-pulse"
      />
    );
  }

  return (
    <div
      ref={setRefs}
      className="relative aspect-[9/16] rounded-xl overflow-hidden shadow-md cursor-ew-resize select-none"
      style={{ touchAction: "pan-y" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* After (full background) */}
      {afterImage && afterImage.length > 0 ? (
        <div className="absolute inset-0" style={{ backgroundImage: `url(${afterImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      ) : (
        <div className="absolute inset-0" style={{ background: afterGradient }} />
      )}

      {/* Before (clipped) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}>
        {beforeImage && beforeImage.length > 0 ? (
          <div className="w-full h-full" style={{ backgroundImage: `url(${beforeImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        ) : (
          <div className="w-full h-full" style={{ background: beforeGradient }} />
        )}
      </div>

      {/* Labels */}
      <span className="absolute top-3 left-3 text-xs font-semibold text-white/80 bg-black/30 px-2 py-0.5 rounded-full">Before</span>
      <span className="absolute top-3 right-3 text-xs font-semibold text-white/80 bg-black/30 px-2 py-0.5 rounded-full">After</span>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white/70"
        style={{ left: `${split}%`, transform: "translateX(-50%)" }}
      />

      {/* Handle */}
      <div
        className="absolute top-1/2 w-8 h-8 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white shadow-lg flex items-center justify-center"
        style={{ left: `${split}%` }}
      >
        <GripVertical className="w-4 h-4 text-gray-600" />
      </div>
    </div>
  );
};

export default ImageCard;
